import {
  IMPERIAL_SCALE_OPTIONS,
  LengthFormat,
  PageFormat,
  resolveLengthFormat,
  UnitSystem,
} from '@ritabygglov/core';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { canvasService } from '../../../apiServices/CanvasService';
import Button, { ButtonType } from '../../../components/Button/Button';
import { siteConfig } from '../../../config/siteConfig';
import { FormatOptions } from '../../../components/Input/FormatOptions';
import { LengthFormatOptions } from '../../../components/Input/LengthFormatOptions';
import {
  Label,
  LabelLg,
  SelectInput,
  TextInput,
} from '../../../components/Input/Input';
import { SCALE_OPTIONS } from '../../../constants';
import { useModal } from '../../../context/Modal/useModal';
import { Header } from '../../../components/Text/Text';
import { useToast } from '../../../context/Toast/useToast';
import { useUser } from '../../../context/User/useUser';
import './NewProjectModal.css';

type EditProjectModalProps = {
  canvasId: string;
};

export const EditProjectModal = ({ canvasId }: EditProjectModalProps) => {
  const { onClose } = useModal();
  const toast = useToast();
  const { canvases, fetchUser } = useUser();
  const canvas = canvases.find((c) => c.canvasId === canvasId);
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [unitSystem, setUnitSystem] = useState<UnitSystem>(
    canvas?.unitSystem || 'metric'
  );
  const [lengthFormat, setLengthFormat] = useState<LengthFormat>(
    resolveLengthFormat(
      canvas?.unitSystem || 'metric',
      canvas?.lengthFormat || 'meters'
    )
  );

  const currentScale = canvas?.drawingHeadScale || '';
  const activeScaleOptions =
    unitSystem === 'imperial'
      ? IMPERIAL_SCALE_OPTIONS.map((option) => ({
          value: option.ratio,
          label: option.label,
        }))
      : SCALE_OPTIONS.map((option) => ({ value: option, label: option }));
  const scaleOptions =
    !currentScale ||
    activeScaleOptions.some((option) => option.value === currentScale)
      ? activeScaleOptions
      : [{ value: currentScale, label: currentScale }, ...activeScaleOptions];

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    setIsLoading(true);
    const data = new FormData(e.currentTarget);

    const form = {
      name: (data.get('name') || '') as string,
      pageFormat: (data.get('pageFormat') || '') as PageFormat,
      drawingHeadScale: (data.get('scale') || '') as string,
      unitSystem: (data.get('unitSystem') || 'metric') as UnitSystem,
      lengthFormat: (data.get('lengthFormat') || 'meters') as LengthFormat,
    };
    canvasService
      .editCanvas(canvasId, form)
      .then(({ message }) => {
        fetchUser();
        onClose();
        toast.addToast({
          message,
          type: 'success',
        });
      })
      .catch((e: Error) => {
        toast.addToast({
          message: e.message,
          type: 'error',
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="formContainer">
      <div className="formItem">
        <Header>{intl.formatMessage({ id: 'editProject.title' })}</Header>
      </div>
      <form onSubmit={onSubmit} className="form">
        <div className="formItem" key="name">
          <LabelLg htmlFor="name">
            {intl.formatMessage({ id: 'editProject.nameLabel' })}
          </LabelLg>
          <TextInput
            defaultValue={canvas?.name}
            type="text"
            name="name"
            id="name"
            required
          />
        </div>
        <div className="formItem">
          <LabelLg htmlFor="unitSystem">
            {intl.formatMessage({ id: 'newProject.unitSystemLabel' })}
          </LabelLg>
          <SelectInput
            value={unitSystem}
            onChange={(e) => {
              const newUnitSystem = e.currentTarget.value as UnitSystem;
              setUnitSystem(newUnitSystem);
              // Resolve the format to one the new unit system can render
              // immediately — otherwise the length-format select below
              // renders blank (its value matches no <option>) and a submit
              // right after switching would write the stale format back.
              setLengthFormat(resolveLengthFormat(newUnitSystem, lengthFormat));
            }}
            name="unitSystem"
            id="unitSystem"
            required
          >
            <option value="metric">
              {intl.formatMessage({ id: 'settings.unitSystemMetric' })}
            </option>
            <option value="imperial">
              {intl.formatMessage({ id: 'settings.unitSystemImperial' })}
            </option>
          </SelectInput>
          <SelectInput
            value={lengthFormat}
            onChange={(e) =>
              setLengthFormat(e.currentTarget.value as LengthFormat)
            }
            name="lengthFormat"
            id="lengthFormat"
            required
          >
            <LengthFormatOptions unitSystem={unitSystem} />
          </SelectInput>
        </div>
        <div className="formItem">
          <LabelLg>
            {intl.formatMessage({ id: 'editProject.scaleLabel' })}
          </LabelLg>
          <Label htmlFor="scale">
            <p>
              {intl.formatMessage({ id: 'editProject.scaleHint' })}{' '}
              <Link target="_blank" to={siteConfig.guideLinks.scale}>
                {intl.formatMessage({ id: 'editProject.scaleReadMore' })}
              </Link>
            </p>
          </Label>
          <SelectInput
            key={`scale-${unitSystem}`}
            defaultValue={canvas?.drawingHeadScale}
            name="scale"
            id="scale"
            required
          >
            {scaleOptions.map((option) => (
              <option value={option.value} key={option.value}>
                {option.label}
              </option>
            ))}
          </SelectInput>
        </div>
        <div className="formItem">
          <LabelLg htmlFor="pageFormat">
            {intl.formatMessage({ id: 'editProject.formatLabel' })}
          </LabelLg>
          <SelectInput
            defaultValue={canvas?.pageFormat}
            name="pageFormat"
            id="pageFormat"
            required
          >
            <FormatOptions />
          </SelectInput>
        </div>
        <div className="formItem" key="submit">
          <div>
            <Button
              type={ButtonType.Primary}
              htmlType="submit"
              loading={isLoading}
              loadingText={intl.formatMessage({
                id: 'editProject.submittingButton',
              })}
            >
              {intl.formatMessage({ id: 'editProject.submitButton' })}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
